import { LitElement, html, css } from 'lit';
import { Router } from '@vaadin/router';
import defaultProfilePicture from './images/default-profile-picture-2.webp';
import { HTTP_CLIENT } from './middleware/axiosClient';

class HeaderBarElement extends LitElement {
  static get properties() {
    return {
      loggedInDetails: { type: Object },
      pageTitle: { type: String },
      showDropdown: { type: Boolean },
    };
  }

  constructor() {
    super();

    this._boundReloadProfile = this.setNewUserInfo.bind(this);

    this.loggedInDetails = {};
    this.pageTitle = '';
    this.showDropdown = false;
  }

  async connectedCallback() {
    super.connectedCallback();

    document.addEventListener('reload-profile', this._boundReloadProfile);

    await this.loadUserInfo();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('reload-profile', this._boundReloadProfile);
  }

  handleDropdownClick() {
    this.showDropdown = !this.showDropdown;
    const chevronIcon = this.shadowRoot.querySelector('.chevron-down-icon');
    chevronIcon.classList.toggle('rotate');
  }

  async handleSignOutClick() {
    try {
      await HTTP_CLIENT.post('/logout-user');
      Router.go('/landing');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  handleToggleClick() {
    this.dispatchEvent(new CustomEvent('toggle-click'));
  }

  async loadUserInfo() {
    try {
      const response = await HTTP_CLIENT.get('/user-info');
      this.loggedInDetails = response.data;
    } catch (error) {
      console.error('Failed to load user information:', error);
    }
  }

  setNewUserInfo({ detail }) {
    this.loggedInDetails = detail;
  }

  static get styles() {
    return css`
      :host {
        display: flex;
        z-index: 1000;
      }

      .header-left {
        float: left;
        margin: 26px 0px 26px 48px;
        color: #2E2E3A;
        font-family: 'Montserrat', sans-serif;
        font-size: 24px;
        font-style: normal;
        font-weight: 600;
        line-height: 32px;
        letter-spacing: -0.24px;

        @media (max-width: 1479px) {
          margin: 26px 0 26px 25px;
        }

        @media (max-width: 1199px) {
          margin: 15px 0 15px 15px;
        }

        @media (max-width: 1023px) {
          display: flex;
          align-items: center;
        }

        .logo {
          display: none;

          @media (max-width: 1023px) {
            display: block;
            width: 120px;
            margin: 0 0 0 -15px;
          }

          img {
            width: 100%;
            height: auto;
            display: block;
          }
        }

        .dashboard-title {
          @media (max-width: 1023px) {
            display: none;
          }
        }
      }

      .nav-opener {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        cursor: pointer;
        color: #000;

        @media (min-width: 1024px) {
          display: none;
        }

        &:hover {
          color: rgb(51, 70, 231);
        }

        &:before,
        &:after,
        span {
          width: 20px;
          height: 2px;
          background: currentColor;
          content: '';
          display: block;
        }

        span {
          margin: 5px 0;
        }
      }

      .header-right {
        display: inline-flex;
        align-items: flex-start;
        float: right;
        margin-right: 48px;
        margin-left: auto;

        @media (max-width: 1479px) {
          margin-right: 25px;
        }

        @media (max-width: 1199px) {
          margin-right: 15px;
        }
      }

      .dropdown-toggle {
        display: flex;
        color: #2E2E3A;
        fill: #2E2E3A;
        align-items: center;
        cursor: pointer;
        margin-top: 15px;

        @media (max-width: 1199px) {
          margin-top: 10px;
        }

        .dropdown-content {
          @media (max-width: 767px) {
            display: none;
          }
        }
      }

      .bubble {
        width: 44px;
        height: 44px;
        border-radius: 24px;
        background: #EFF1F7;
        margin-top: 15px;
        margin-right: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 1199px) {
          margin-top: 12px;
          margin-right: 15px;
          width: 36px;
          height: 36px;
        }
      }

      .bubble img {
        width: 20px;
        height: 20px;
        display: block;
      }

      .profile-picture {
        width: 44px;
        height: 44px;
        border-radius: 44px;
        margin-right: 10px;
        border: 1px solid #ccc;
      
        @media (max-width: 1199px) {
          width: 36px;
          height: 36px;
        }
      
        @media (max-width: 767px) {
          margin-right: 0;
        }
      }

      .chevron-down-icon {
        display: flex;
        align-items: center;
        width: 24px;
        height: 24px;
        margin-top: 8px;
        margin-left: 10px;
        margin-bottom: 8px;
        transition: transform 0.2s ease-in-out;

        @media (max-width: 767px) {
          margin-left: 5px;
        }
      }

      .chevron-down-icon.rotate {
        transform: rotate(180deg);
      }

      .name {
        color: #2E2E3A;
        font-family: 'Montserrat', sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.16px;
        margin-bottom: 3px;
      }

      .email {
        color: #535362;
        font-family: 'Montserrat', sans-serif;
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 12px;
        letter-spacing: -0.12px;
      }

      .dropdown {
        position: relative;
      }

      .dropdown-menu {
        position: absolute;
        width: 180px;
        top: 100%;
        background-color: #ffffff;
        border: 1px solid #cccccc;
        border-radius: 5px;
        padding: 10px;
        z-index: 10;
        pointer-events: auto;
        transition: opacity 0.2s ease-in-out;
        right: 0;
        margin: 20px 0 0;

        @media (max-width: 767px) {
          margin: 10px 0 0;
        }
      }

      .dropdown-menu::before,
      .dropdown-menu::after {
        content: "";
        display: block;
        position: absolute;
        border-style: solid;
        border-width: 10px;
        left: 80%;
      }

      .dropdown-menu::before {
        border-color: transparent transparent #cccccc transparent;
        top: -20px;
      }

      .dropdown-menu::after {
        border-color: transparent transparent #ffffff transparent;
        top: -18px;
        z-index: 1;
      }

      .dropdown-item {
        display: block;
        padding: 5px 0;
        cursor: pointer;
      }

      .dropdown-item:hover {
        background-color: #f0f0f0;
      }
    `;
  }
  
  render() {
    return html`
      <div class="header-left">
        <div class="logo">
          <img alt="Logo" src="/images/logo.webp">
        </div>
        <div class="nav-opener" @click="${this.handleToggleClick}"><span></span></div>
        <span class="dashboard-title">${this.pageTitle}</span>
      </div>
      <div class="header-right">
        ${this.loggedInDetails ? html`
          <div class="dropdown">
            <div class="dropdown-toggle" @click="${this.handleDropdownClick}">
              <img 
                src="${this.loggedInDetails.profilePictureUrl || defaultProfilePicture}" 
                alt="Profile Picture" 
                class="profile-picture" 
                referrerpolicy="no-referrer">
              <div class="dropdown-content">
                <div class="name">${this.loggedInDetails.firstName} ${this.loggedInDetails.lastName}</div>
                <div class="email">${this.loggedInDetails.email}</div>
              </div>
              <div class="chevron-down-icon">
                <box-icon name='chevron-down' name="chevron-down"></box-icon>
              </div>
            </div>
            ${this.showDropdown ? html`
              <div class="dropdown-menu">
                <div class="dropdown-item" @click="${this.handleSignOutClick}">Sign out</div>
              </div>
            ` : null}
          </div>
        ` : null}
      </div>
    `;
  }
}

customElements.define('header-bar-element', HeaderBarElement);